import { CARDS, type LensFilters, VIEWS, type ViewId } from './liveDataAdapter';

/**
 * The Tableau workbook's quick filters, per worksheet. Ids and labels match the Lens spec in
 * svc-reporting (accommodation-views-impressions-sales.json); the values come back from Lens.
 * Every control is a multi-select that starts empty, which means All, as Tableau's saved view does
 * for these fields, so the default numbers are unchanged.
 */
export const SELECTION_FILTERS = {
  [VIEWS[0]]: [
    { id: 'conv_website_region', label: 'Website Region' },
    { id: 'conv_lp_offer_flag', label: 'LP+ Offer Eligible Flag' },
    { id: 'conv_lp_benefit', label: 'LP+ Offer Eligible' },
    { id: 'conv_category', label: 'Category Name' },
    { id: 'conv_product', label: 'Product Name' },
    { id: 'conv_page_type', label: 'Product Page Type' },
    { id: 'conv_chain', label: 'Chains' },
    { id: 'conv_vendor', label: 'Vendor Name' },
    { id: 'conv_property_country', label: 'Property Country' },
    { id: 'conv_property_state', label: 'Property State' },
    { id: 'conv_property_city', label: 'Property City' },
    { id: 'conv_spp_property', label: 'SPP Property Name' },
    { id: 'conv_property_chain', label: 'Property Chain Name' },
    { id: 'conv_offer_owner', label: 'Offer Owner Name' },
    { id: 'conv_brand', label: 'Brand Name' },
    { id: 'conv_traffic_channel', label: 'Traffic Channel' },
    { id: 'conv_lead_time', label: 'Lead Time' },
  ],
  [VIEWS[1]]: [
    { id: 'sup_supplier', label: 'Supplier' },
    { id: 'sup_board', label: 'Board Code' },
    { id: 'sup_refund', label: 'Refund Flag' },
    { id: 'sup_rate_plan_code', label: 'Rate Plan Code' },
    { id: 'sup_rate_plan_name', label: 'Rate Plan Name' },
    { id: 'sup_property', label: 'Property Name' },
    { id: 'sup_property_country', label: 'Property Country' },
    { id: 'sup_property_city', label: 'Property City' },
    { id: 'sup_property_chain', label: 'Property Chain' },
    { id: 'sup_brand', label: 'Brand' },
    { id: 'sup_lead_time', label: 'Lead Time' },
  ],
  [VIEWS[2]]: [
    { id: 'lpc_vendor', label: 'Vendor Name' },
    { id: 'lpc_country', label: 'Offer Country Name' },
    { id: 'lpc_page_type', label: 'Page Type' },
    { id: 'lpc_campaign_sources', label: 'Tactical Campaign Sources' },
    { id: 'lpc_flash_on', label: 'Concurrent Flash Deal On' },
    { id: 'lpc_rates_available', label: 'LPC Package Rates Available' },
    { id: 'lpc_tactical_flag', label: 'Tactical Campaign Flag' },
    { id: 'lpc_tactical_offer_on', label: 'Tactical Offer On (SF)' },
  ],
  [VIEWS[3]]: [
    { id: 'tac_vendor', label: 'Vendor Name' },
    { id: 'tac_campaign_source', label: 'Campaign Source' },
    { id: 'tac_campaign_name', label: 'Campaign Name' },
    { id: 'tac_promotion_length', label: 'Promotion Length' },
    { id: 'tac_country', label: 'Offer Country Name' },
  ],
} as const satisfies Record<ViewId, ReadonlyArray<{ id: string; label: string }>>;

export type SelectionId = (typeof SELECTION_FILTERS)[ViewId][number]['id'];
export type Selections = Partial<Record<SelectionId, Array<string>>>;

export const SELECTION_IDS: ReadonlyArray<SelectionId> = VIEWS.flatMap((view) =>
  SELECTION_FILTERS[view].map((filter) => filter.id),
);

/** Lens lists every control's values when it is asked for them; only the tactical card is cheap to load with them. */
export const OPTIONS_CARD_IDS = [CARDS.tactical];

/** The worksheet cards. The spec's filter-value cards feed the dropdown lists and are never shown. */
export const REPORT_CARD_IDS = Object.values(CARDS);

/** The options loader in svc-reporting returns at most this many values per control. */
export const OPTION_LIMIT = 500;

const PERIOD_KEYS = ['cp_start', 'cp_end', 'pp_start', 'pp_end', 'comparison'] as const;

/** Values are stored in the URL as a JSON array per filter id, the same as Product Performance. */
export function readSelections(params: URLSearchParams): Selections {
  const selections: Selections = {};
  SELECTION_IDS.forEach((id) => {
    const raw = params.get(id);
    if (!raw) {
      return;
    }
    try {
      const value: unknown = JSON.parse(raw);
      if (Array.isArray(value) && value.length && value.every((item) => typeof item === 'string')) {
        selections[id] = [...new Set(value as Array<string>)];
      }
    } catch {
      // A hand-edited or truncated link falls back to All for that control.
    }
  });
  return selections;
}

export function writeSelections(selections: Selections, current: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams(current);
  SELECTION_IDS.forEach((id) => {
    const values = [...new Set(selections[id] ?? [])].sort();
    if (values.length) {
      params.set(id, JSON.stringify(values));
    } else {
      params.delete(id);
    }
  });
  return params;
}

/** The report request: the page's own controls plus every non-empty selection. An empty selection is All. */
export function reportRequest(filters: LensFilters, selections: Selections): Record<string, string | Array<string>> {
  const request: Record<string, string | Array<string>> = { ...filters };
  SELECTION_IDS.forEach((id) => {
    if (selections[id]?.length) {
      request[id] = selections[id];
    }
  });
  return request;
}

/**
 * The dropdown request depends only on the reporting window, so changing a selection does not
 * reload every list. Lists are therefore not narrowed by the other selections.
 */
export function optionsRequest(filters: LensFilters): Record<string, string> {
  return Object.fromEntries(PERIOD_KEYS.map((key) => [key, filters[key]]));
}

export function activeSelectionCount(view: ViewId, selections: Selections): number {
  return SELECTION_FILTERS[view].filter((filter) => selections[filter.id]?.length).length;
}
